//@ts-nocheck
import React, { useEffect, useRef, useState, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { fetchQuestionSetAnswerData, updateQuestionSetAnswerData } from '../../../features/applicationQuestionnaire/applicationQuestionnaireSlice';
import { motion, AnimatePresence } from "framer-motion";
import CustomMenuDropdown from '../../common/CustomMenuDropdown';
import { createAppScoreCardDataThunk, fetchScorecardByListDataFilter } from '../../../features/ApplicationData/ApplicationDataSlice';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronsUpDownIcon } from 'lucide-react';
import { Transition } from '@headlessui/react';
import { toast } from 'sonner';
import { fetchAppSkillRatingByDetails } from '../../../features/AppSkillRating/AppSkillRatingSlice';
import { getColorForLabel } from '../../../utils/colorUtils';


function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ')
}


// Function to determine badge color based on skill scale value
const getSkillScaleBadgeStyles = (scale) => {

  // Parse the scale value to ensure it's a number
  const scaleValue = parseFloat(scale);

  // Set badge styles based on ranges
  if (scaleValue >= 3.5 && scaleValue <= 4.0) {
    // Expert: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (scaleValue >= 1.5 && scaleValue <= 3.4) {
    // Proficient: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (scaleValue >= 0.5 && scaleValue <= 1.4) {
    // Competent: Yellow
    return {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      ring: "ring-yellow-700/10"
    };
  } else if (scaleValue >= -1.5 && scaleValue <= 0.4) {
    // Limited: Orange
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  } else if (scaleValue >= -2.0 && scaleValue <= -1.6) {
    // Inadequate: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};

// Function to get skill level label based on scale value
const getSkillLevelLabel = (scale) => {
  const scaleValue = parseFloat(scale);
  if (scaleValue >= 3.5 && scaleValue <= 4.0) return "Expert";
  if (scaleValue >= 1.5 && scaleValue <= 3.4) return "Proficient";
  if (scaleValue >= 0.5 && scaleValue <= 1.4) return "Competent";
  if (scaleValue >= -1.5 && scaleValue <= 0.4) return "Limited";
  if (scaleValue >= -2.0 && scaleValue <= -1.6) return "Inadequate";

  return null;
};

// Function to get badge styles based on rating value
const getRatingBadgeStyles = (rating) => {
  if (!rating) return {
    bg: "bg-gray-50",
    text: "text-gray-700",
    ring: "ring-gray-700/10"
  };

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) {
    // Strong Yes: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (ratingValue === 4) {
    // Yes: Light Green
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      ring: "ring-emerald-700/10"
    };
  } else if (ratingValue === 3) {
    // Neutral: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (ratingValue === 2) {
    // No: Orange
    return {
      bg: "bg-orange-50",
      text: "text-orange-700",
      ring: "ring-orange-700/10"
    };
  } else if (ratingValue === 1) {
    // Strong No: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};

const getRatingLabel = (rating) => {
  if (!rating) return null;

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) return "Strong Yes";
  if (ratingValue === 4) return "Yes";
  if (ratingValue === 3) return "Neutral";
  if (ratingValue === 2) return "No";
  if (ratingValue === 1) return "Strong No";

  return null;
};




const AutoResizeTextarea = ({ value, onChange, placeholder, hasError }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = "auto";
      // Set the height based on scrollHeight with a minimum of 60px
      textareaRef.current.style.height = `${Math.max(60, textareaRef.current.scrollHeight)}px`;
    }
  }, [value]);



  return (
    <div className="w-full">
      <textarea
        ref={textareaRef}
        className={`outline-none bg-transparent focus:outline-none focus:ring-0 text-[14px] text-dark-black-100 w-full m-0 placeholder:text-[#9e9ea0]
        border-1 border-solid ${hasError ? 'border-red-500' : 'border-light-gray-200'} rounded-md p-2 min-h-[60px] overflow-hidden resize-none leading-5`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        rows={1}
        maxLength={1000}
      />
      {hasError && <p className="text-red-500 text-xs mt-1">This field is required</p>}
    </div>
  );
};

const ratingOptions = [
  { value: 1, label: "Strong No" },
  { value: 2, label: "No" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Yes" },
  { value: 5, label: "Strong Yes" },
];


const RatingButton = ({ label, value, selectedValue, onClick }) => {
  const isSelected = selectedValue == value;

  return (
    <button
      className={`flex items-center gap-[6px] text-[13px] rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${isSelected
        ? 'bg-indigo-600 text-white ring-1 ring-inset ring-indigo-600'
        : 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'
        }`}
      onClick={() => onClick(value)}
      type="button"
    >
      {label}
    </button>
  );
};


// Individual circular progress component
const CircularRatingProgress = ({ rating, maxRating = 5, size = 36, strokeWidth = 5, color = '#3b82f6' }) => {
  // Handle null or undefined ratings and format to 1 decimal place
  const actualRating = rating ? parseFloat(rating).toFixed(1) : '0.0';

  // Calculate percentage
  const percentage = (parseFloat(actualRating) / maxRating) * 100;

  // Calculate circle properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Center position
  const center = size / 2;

  return (
    <div className="flex items-center justify-center bg-white rounded-lg">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg width={size} height={size}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e6e6e6"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        </svg>

        {/* Score text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[10px] leading-4 font-normal" style={{ color }}>
            {actualRating}
          </span>
        </div>
      </div>
    </div>
  );
};


// Function to determine rating color
const getRatingColor = (rating) => {
  if (!rating) return '#9ca3af'; // Gray for null ratings
  const score = parseFloat(rating);
  if (score >= 4) return '#10b981'; // Green for high ratings
  if (score >= 2.5) return '#f59e0b'; // Yellow/orange for medium
  return '#ef4444'; // Red for low ratings
};



function QuestionSetDetails({ QuestionsId, job: jobProp, app: appProp, quickData }) {
  const [loading, setLoading] = useState(true);
  // const { baseId, jobId, applicationId, questionnaireId } = useParams<{ baseId: string; jobId: string; applicationId: string; questionnaireId: string }>();

  const { baseId, jobId: jobIdFromParams, applicationId: applicationIdFromParams, questionnaireId } = useParams<{
    baseId: string;
    jobId: string;
    applicationId: string;
    questionnaireId: string
  }>();

  const jobId = jobProp || jobIdFromParams;
  const applicationId = appProp || applicationIdFromParams || quickData

  const dispatch = useAppDispatch();
  const [answerData, setAnswerData] = useState(null);
  const [scorecard, setScorecard] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState(null);
  const [overallRating, setOverallRating] = useState(null);
  const [overallRatingError, setOverallRatingError] = useState(false);
  const [overallComment, setOverallComment] = useState("");
  const [savingData, setSavingData] = useState(false);
  const [openAccordionIds1CandidateCreate, setOpenAccordionIds1CandidateCreate] = useState({});
  const [skillsData, setSkillsData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [commentRequiredSkills, setCommentRequiredSkills] = useState({});
  const userDetailsData = localStorage.getItem('jobByHiringTeamMembers');
  const baseMembers = userDetailsData ? JSON.parse(userDetailsData) : [];
  const [openAccordionIds1, setOpenAccordionIds1] = useState({});
  const [loadingAppScorecards, setLoadingAppScorecards] = useState({});
  const [appScorecardData, setAppScorecardData] = useState([])
  const navigate = useNavigate()
  const [overallComment1, setOverallComment1] = useState("");
  const [savingCommentData, setSavingCommentData] = useState(false);



  // Function to parse and map answers to questions
  const getAnswerForQuestion = (questionId) => {
    if (!answerData?.answer) return null;
    try {
      // Parse the answer JSON string
      const answers = JSON.parse(answerData.answer);

      // Find the answer for this question ID
      const answerObj = answers.find(answer => answer.id === questionId);

      if (!answerObj) return null;

      // Handle different types of answer values
      const { value } = answerObj;
      ;

      // If value is an array (for multiple choice or complex answers)
      if (Array.isArray(value)) {
        if (value.length === 0) return "-";
        // If it's an array of objects with value property
        if (value[0] && typeof value[0] === 'object' && value[0].value) {
          return value.map(item => item.value).join(', ');
        }
        // If it's just an array of strings
        return value.join(', ');
      }


      // If value is undefined or null
      if (value === undefined || value === null || value === '') {
        return "-";
      }

      // Return the value as string
      return String(value);

    } catch (error) {
      console.error('Error parsing answers:', error);
      return "Error loading answer";
    }
  };

  // Function to format answer display based on content
  const formatAnswerDisplay = (answer) => {
    if (!answer || answer === "-") {
      return (
        <span className="text-gray-400 text-sm">
          -
        </span>
      );
    }

    if (answer === "Error loading answer") {
      return (
        <span className="text-red-400 text-sm">
          Error loading answer
        </span>
      );
    }

    return (
      <>
        {answer}
      </>
    );
  };


  const getStageName = (stageId) => {
    // Get scorecard data from localStorage
    const scorecardData = JSON.parse(localStorage.getItem("jobPipelineStageData") || "{}");
    const stages = scorecardData?.stages || [];

    // Find the stage with matching ID
    const stage = stages.find(stage => stage.id === stageId);
    return stage ? stage.stage_name : null;
  };

  // Function to get user name by ID
  const getUserName = (userId) => {
    const user = baseMembers.find(member => member.user_id === userId || member.user_id === Number(userId));
    return user ? user.name : `User ${userId}`;
  };

  const getUserProfileImage = (userId) => {
    const user = baseMembers.find(member => member.user_id === userId || member.user_id === Number(userId));
    return user ? user.profile_pic : null;
  }



  useEffect(() => {
    const fetchScorecard = async () => {
      if (baseId && QuestionsId) {
        setLoading(true);
        try {
          const res = await dispatch(
            fetchQuestionSetAnswerData({
              baseId: Number(baseId),
              questionnaireId: QuestionsId,
            })
          ).unwrap();
          if (res.status === 200) {
            setAnswerData(res.data);
          }
        } catch (error) {
          console.error("Error fetching scorecard:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchScorecard();
  }, [baseId, QuestionsId, dispatch]);


  // scorecard 


  const handleScorecardChange = (scorecardId) => {
    const selected = scorecard.find(card => card.id === scorecardId);
    setSelectedScorecard(selected);
  };

  // 3. Prepare the dropdown options - grouping scorecards by type
  const getScorecardOptions = () => {
    // Make sure scorecard is an array before trying to filter it
    if (!Array.isArray(scorecard) || scorecard.length === 0) return [];

    const candidateScoreCards = scorecard.filter(card => card.type === 'candidate');
    const skillScoreCards = scorecard.filter(card => card.type === 'skill');

    return [
      {
        label: 'Candidate Scorecards',
        items: candidateScoreCards.map(card => ({
          id: card.id,
          label: card.scorecard_name,
          scorecard_colour: card.scorecard_colour,
          description: card.scorecard_description,
          onClick: () => handleScorecardChange(card.id)
        }))
      },
      {
        label: 'Skill Scorecards',
        items: skillScoreCards.map(card => ({
          id: card.id,
          label: card.scorecard_name,
          scorecard_colour: card.scorecard_colour,
          description: card.scorecard_description,
          onClick: () => handleScorecardChange(card.id)
        }))
      }
    ];
  };

  const ScorecardDropdown = () => {
    const dropdownOptions = getScorecardOptions();
    const [selected, setSelected] = useState(selectedScorecard);

    // Update selected when selectedScorecard changes
    useEffect(() => {
      setSelected(selectedScorecard);
    }, [selectedScorecard]);

    if (!dropdownOptions.length) return null;

    // Flatten the options for the listbox
    const flattenedOptions = dropdownOptions.flatMap(section => section.items);

    const handleChange = (option) => {
      setSelected(option);
      // Call the original onClick handler from the option
      option.onClick();
    };

    return (
      <div className="mt-0 mb-6">
        <div className="flex items-start gap-2 flex-col justify-start">
          <span className="text-sm font-medium text-gray-700">Scorecard:</span>
          <div className="relative w-full">
            <Listbox value={selected} onChange={handleChange}>
              <div className="relative mt-0">
                <Listbox.Button className=" w-[100%] inline-flex items-center justify-start whitespace-nowrap rounded-md text-sm font-medium transition-colors outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50  [&_svg]:pointer-events-none [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-8 px-4  py-2">
                  <span className="flex items-center gap-2">
                    {selected ? (
                      <>
                        <div
                          className="h-[8px] w-[8px] rounded-[2px]"
                          style={{ backgroundColor: selected.scorecard_colour || '#3b82f6' }}
                        ></div>
                        <span className="block truncate text-[#1b1b1b] font-normal capitalize">{selected.scorecard_name}</span>
                      </>
                    ) : (
                      <span className="block truncate text-[#1b1b1b] font-normal">Select Scorecard</span>
                    )}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-[10px] flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[1rem] h-[1rem] text-[#808080]"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M6 9l6 6l6 -6" />
                    </svg>
                  </span>

                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-[9px] left-[-2px] bg-white  w-full overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg shadow-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ">
                    {dropdownOptions.map((section, sectionIdx) => (
                      <div key={sectionIdx}>
                        {/* <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50">
                        {section.label}
                      </div> */}
                        {section.items.map((option, optionIdx) => (
                          <Listbox.Option
                            key={optionIdx}

                            className={({ active }) =>
                              classNames(
                                active ? 'bg-gray-200 text-[#1d2025]' : 'text-gray-900',
                                'relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0'
                              )
                            }
                            value={option}
                          >
                            {({ selected, active }) => (
                              <>
                                <span
                                  className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                                    }`}
                                >
                                  {option.label}
                                </span>
                                {selected ? (
                                  <span
                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-indigo-600' : 'text-indigo-600'
                                      }`}
                                  >
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </div>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>
        </div>

      </div>
    );
  };


  useEffect(() => {
    const fetchScorecard = async () => {
      if (baseId && applicationId) {
        setLoading(true);
        try {
          const res = await dispatch(
            fetchScorecardByListDataFilter({
              baseId: Number(baseId),
              appId: applicationId
            })
          ).unwrap();
          if (res.status === 200) {
            // Combine the candidate and skill arrays from the response
            const allScorecards = [...res.data.candidate, ...res.data.skill];
            setScorecard(allScorecards);

            // Set the first scorecard as selected by default if one exists and matches answerData.scorecard_id
            if (allScorecards && allScorecards.length > 0) {
              const defaultScorecard = answerData && answerData.scorecard_id
                ? allScorecards.find(card => card.id === answerData.scorecard_id)
                : allScorecards[0];
              setSelectedScorecard(defaultScorecard);
            }
          }
        } catch (error) {
          console.error("Error fetching scorecard:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchScorecard();
  }, [baseId, applicationId, dispatch]);



  const [isAccordionOpenCandidateCreate, setIsAccordionOpenCandidateCreate] = useState(false);
  const [activeScorecardId, setActiveScorecardId] = useState(null);

  const handleCreateCandidate = (scorecardId, scorecard) => {
    setActiveScorecardId(scorecardId);
    setIsAccordionOpenCandidateCreate(prev =>
      prev && activeScorecardId === scorecardId ? false : true
    );

    // Additional logic for handling the scorecard...
  };

  const handleOverallRatingSelect = (value) => {
    setOverallRating(value);
  };

  // Handle overall comment change
  const handleOverallCommentChange = (e) => {
    setOverallComment(e.target.value);
  };

  const handleSaveScorecardCandidateCreate = async (data) => {

    if (overallRating === null) {
      setOverallRatingError(true);
      return;
    }

    setOverallRatingError(false);

    setSavingData(true);
    try {
      const payload = {
        app_id: applicationId,
        scorecard_id: data.id || skillId,
        comment: overallComment,
        rating: overallRating

      };

      const response = await dispatch(createAppScoreCardDataThunk({
        baseId: Number(baseId),
        data: payload
      }));

      if (response?.payload.status === 200) {
        toast.success("Candidate app scorecard saved successfully!");
        setOverallComment('')
        // Reload the data
        const res = await dispatch(
          fetchScorecardByListDataFilter({
            baseId: Number(baseId),
            appId: applicationId
          })
        ).unwrap();
        if (res.status === 200) {
          setScorecard(res.data);
          setOpenAccordionIds1CandidateCreate(false)
        }

      } else {
        // Handle API error
        if (response?.error?.message) {
          toast.error('Rating should be selected' || `Error: ${response.error.message}`);
        } else {
          throw new Error("Failed to save candidate app scorecard");
        }
      }
    } catch (error) {
      console.error("Error saving scorecard:", error);
      toast.error("Failed to save app scorecard. Please try again.");
    } finally {
      setSavingData(false);
    }
  };


  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    let overallCommentRequired = false;

    // Check if each skill has a rating
    selectedScorecard.skills.forEach(skill => {
      if (!skillsData[skill.id]?.rating) {
        errors[`rating_${skill.id}`] = true;
        isValid = false;
      }

      // Check if comment is required for this skill
      if (commentRequiredSkills[skill.id] && !skillsData[skill.id]?.comment.trim()) {
        errors[`comment_${skill.id}`] = true;
        isValid = false;
      }
    });

    // // Check if overall rating is selected
    // if (!overallRating) {
    //   setOverallRatingError(true);
    //   isValid = false;
    // }

    // Check if overall comment is required
    if (overallCommentRequired && !overallComment.trim()) {
      setOverallCommentError(true);
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };



  // Handle saving scorecard
  const handleSaveScorecard = async () => {
    // Validate form before submission
    if (!validateForm()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSavingData(true);
    try {
      // Prepare skill ratings data for submission
      const skillRatings = Object.entries(skillsData).map(([skillId, data]) => ({
        skill_id: skillId,
        comment: data.comment || "",
        skill_scale: data.rating // 1-5 values as per the requirements
      }));

      // Prepare the payload according to the required format
      const payload = {
        app_id: applicationId,
        scorecard_id: skillId, // Assuming skillId is the scorecard_id
        comment: overallComment,
        // rating: overallRating,
        skill_rating: skillRatings
      };

      // Call the API to save scorecard data
      const response = await dispatch(createAppScoreCardDataThunk({
        baseId: Number(baseId),
        data: payload
      }));

      if (response?.payload) {
        toast.success("App scorecard saved successfully!");
      } else {
        // Handle API error
        if (response?.error?.message) {
          toast.error(`Error: ${response.error.message}`);
        } else {
          throw new Error("Failed to save app scorecard");
        }
      }
    } catch (error) {
      console.error("Error saving scorecard:", error);
      toast.error("Failed to save app scorecard. Please try again.");
    } finally {
      setSavingData(false);
    }
  };


  const handleAppSkillRating = (id, data) => {
    // Check if this accordion is already open
    const isCurrentlyOpen = openAccordionIds1[id] || false;

    // Close all accordions and only open the current one if it wasn't already open
    setOpenAccordionIds1((prev) => {
      // Start with an empty object (all accordions closed)
      const newState = {};

      // Only set the current accordion to open if it was previously closed
      if (!isCurrentlyOpen) {
        newState[id] = true;

        // Only fetch data if we're opening this accordion
        setLoadingAppScorecards(prev => ({
          ...prev,
          [id]: true
        }));

        if (baseId && applicationId && id) {
          dispatch(fetchAppSkillRatingByDetails({
            baseId: Number(baseId),
            appId: applicationId,
            appScoreId: id
          }))
            .unwrap()
            .then((response) => {
              if (response.status === 200) {
                setAppScorecardData(response.data.skillReting);
              }
            })
            .catch((error) => {
              console.error(`Error fetching skill rating details:`, error);
            })
            .finally(() => {
              setLoadingAppScorecards(prev => ({
                ...prev,
                [id]: false
              }));
            });
        }
      }

      return newState;
    });
  }


  const handleCreateSkillOpen = (id) => {
    navigate(`/${baseId}/job/${jobId}/app/${applicationId}/scorecards/${id}`)
  }


  const handleOverallCommentChange1 = (e) => {
    setOverallComment1(e.target.value);
  };

  // set overallComment1 in useeected in answerData
  useEffect(() => {
    if (answerData && answerData.comment) {
      setOverallComment1(answerData.comment);
    }
  }, [answerData]);

  const handleSaveComment = async () => {
    if (!overallComment1.trim()) {
      toast.error("Overall comment is required.");
      return;
    }

    setSavingCommentData(true);

    try {
      const payload = {
        app_id: applicationId,
        job_id: jobId,
        comment: overallComment1,
        review_status: "done"
      };

      const response = await dispatch(
        updateQuestionSetAnswerData({
          baseId: Number(baseId),
          data: payload,
          questionnaireId: answerData?.id,
        })
      );
      if (response?.payload?.status === 200) {
        toast.success("Comment saved successfully.");
        setOverallComment1('')
        const res = await dispatch(
          fetchQuestionSetAnswerData({
            baseId: Number(baseId),
            questionnaireId: QuestionsId,
          })
        ).unwrap();
        if (res.status === 200) {
          setAnswerData(res.data);
        }
      }
      // Optionally, you can handle success response here

    } catch (error) {
      console.error("Failed to save comment:", error);
      toast.error("Failed to save comment.");
    } finally {
      setSavingCommentData(false);
    }
  };

  const reviewerName = getUserName(answerData?.assigned_to);
  const reviewerProfileImage = getUserProfileImage(answerData?.assigned_to);

  if (loading) {
    return (
      <div className="flex items-center justify-center relative z-50" style={{ height: "100px" }}>
        <div className="simple-spinner-loader">
          <span></span>
        </div>
      </div>
    );
  }

  if (!answerData) {
    return <div className="w-full flex h-[100px] justify-center items-center flex-col gap-y-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={24}
        height={24}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-info-triangle  w-[1.5rem] h-[1.5rem] text-[#808080]"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
        <path d="M12 9h.01" />
        <path d="M11 12h1v4h1" />
      </svg>
      <p className="text-[#0000007a] text-[13px]">No results found</p>
    </div>;
  }

  return (
    <div className="p-0">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex h-[8px] w-[8px] shrink-0 items-center justify-center rounded-[2px] border-none text-[0.625rem] font-medium text-white capitalize" style={{ backgroundColor: answerData.questionset_colour }}></div>
        <h2 className="text-lg font-semibold capitalize">{answerData.questionset_name}</h2>
      </div>
      <p className="text-gray-600 text-sm capitalize">{answerData.questionset_description}</p>
      <div className='bg-[#e0e0e0] w-full h-[1px] mt-7 mb-8'></div>

      {/* Questions List */}
      <ul className="space-y-4">
        {answerData.questions && answerData.questions.map((question, index) => {

          const answer = getAnswerForQuestion(question.id);

          return (
            <li key={question.id} className="relative flex gap-x-4">
              <div className="relative flex-none w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-xs font-medium text-gray-700">{question.question_order}</span>
              </div>
              <div className="flex-auto rounded-md p-4 ring-1 ring-inset ring-gray-200">
                <div className="w-full flex flex-col gap-2">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <div className="flex flex-col gap-y-1">
                        <div className="flex items-center gap-x-1">
                          <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4">
                            {question.question_title}
                          </span>
                        </div>

                        <div className="bg-gray-200 w-full h-px my-2"></div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[0.8125rem] font-medium leading-4 text-[#1b1b1b] capitalize">
                            {formatAnswerDisplay(answer)}

                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>



      {/* <div className='bg-[#e0e0e0] w-full h-[1px] mt-8 mb-8'></div>


      <div className="mt-0 mb-6">
        <div className="flex items-start gap-2 flex-col justify-start">
          <span className="text-sm font-medium text-gray-700">Comment:</span>
          {
            answerData?.review_status === 'done' ? (
              <>
                {
                  answerData?.assigned_to &&
           <div className='flex items-center gap-2'>
                   <div className='ml-[0px] table-tooltip-list before:!bottom-[17px] before:!left-[11px]' 
                  //  data-title={reviewerName}
                   >
                    {
                      reviewerProfileImage ? (
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/${reviewerProfileImage}`}
                          alt={`${'Profile'}`}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div style={{ backgroundColor: getColorForLabel(reviewerName?.charAt(0)) }}
                          className="w-6 h-6 rounded-full flex items-center justify-center">
                          <span className="font-medium text-white text-[11px]"
                          >{reviewerName?.charAt(0)}</span>
                        </div>
                      )
                    }
                 
                  </div>
                   <p className="text-[12px] text-[#374151] font-medium leading-5 capitalize mt-0">
                    {reviewerName}
                  </p>
           </div>
                }




                <p className="text-sm text-gray-500">{overallComment1 || "No comment provided."}</p>
              </>

            ) : (
              <>
                <AutoResizeTextarea
                  value={overallComment1}
                  onChange={(e) => setOverallComment1(e.target.value)}
                  placeholder="Write a comment"
                />
                <div className='flex justify-end w-full'>
                  <button
                    onClick={handleSaveComment}
                    className='group inline-flex shrink-0 w-fit select-none justify-center items-center px-[14px] py-1 text-[13px] font-medium h-[32px] leading-6 transition-colors
                             duration-100 wg-antialiased focus:outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none gap-0 
                           rounded-md text-white outline-indigo-600 disabled:opacity-50 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600'
                  >
                    {savingCommentData ? (
                      <div className='flex items-center  gap-[6px]'>
                        Save
                        <div className="simple-spinner">
                          <span></span>
                        </div>
                      </div>
                    ) : (
                      ' Save'
                    )}
                  </button>

                </div>
              </>
            )
          }


        </div>
      </div> */}






      {/* <ScorecardDropdown /> */}



      {/* {selectedScorecard && (
        <div className="space-y-4">
          <div className="rounded-md cursor-pointer ring-1 ring-gray-200 feedback-delete-hover">
            <div className="flex items-center">
              <div className="w-[70%] flex flex-col gap-1 px-4 pt-[15px] pb-[15px]">
                <div className="flex justify-start items-center gap-x-2">
                  <span className="flex items-center gap-x-3">
                    <span className="flex h-[8px] w-[8px] shrink-0 items-center justify-center rounded-[2px] border-none text-[0.625rem] font-medium text-white capitalize"
                      style={{ backgroundColor: selectedScorecard.scorecard_colour }}
                    >
                    </span>
                  </span>
                  <div className="flex gap-x-[6px] items-center">
                    <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize">
                      {selectedScorecard.scorecard_name}
                    </span>
                  </div>
                </div>
                <p className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4">
                  {selectedScorecard.scorecard_description}
                </p>
              </div>
              <div className="w-[30%] pr-4 mr-0 flex items-center gap-x-4 justify-end">


                <div className="flex shrink-0">
                  <div
                    className="h-[36px] w-[36px] rounded-[8px] flex flex-row items-center justify-center text-white"

                  >
                    <CircularRatingProgress
                      rating={selectedScorecard.appScorecards[0]?.rating_average}
                      color={getRatingColor(selectedScorecard.appScorecards[0]?.rating_average)}
                    />
                  </div>
                </div>


                <span className="table-tooltip before:!bottom-[28px] before:!left-[28px]"
                  data-title='score'>
                  <button
                    onClick={() => handleCreateCandidate(selectedScorecard.id, selectedScorecard)}
                    className="rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                   outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                   [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 text-[#808080] w-auto py-1 px-2 gap-x-2">
                    Score
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-arrow-narrow-right w-[0.9rem] h-[0.9rem] text-[#808080]"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M5 12l14 0" />
                      <path d="M15 16l4 -4" />
                      <path d="M15 8l4 4" />
                    </svg>

                  </button>
                </span>
              </div>
            </div>

          
            <AnimatePresence>
              {isAccordionOpenCandidateCreate && selectedScorecard.id === activeScorecardId && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className='bg-[#e0e0e0] w-full h-[1px]'></div>


                  {selectedScorecard.skills.length > 0 ? (
                    <>
                      {selectedScorecard.skills.map((skill) => (
                        <div key={skill.id} className=" cursor-pointer feedback-delete-hover border-b border-solid border-[#e0e0e0] mx-4 last-of-type:mx-0 last:border-none">
                          <div className="flex items-center">
                            <div className="w-[100%] flex flex-col gap-1 px-0 pt-[15px] pb-[15px]">
                              <div className="flex justify-start items-center gap-x-2">
                                <span className="flex items-center gap-x-3">
                                  <span
                                    className="flex h-[8px] w-[8px] shrink-0 items-center justify-center rounded-[2px] border-none text-[0.625rem] font-medium text-white capitalize"
                                    style={{ backgroundColor: skill.skill_colour }}
                                  />
                                </span>
                                <div className="flex gap-x-[6px] items-center">
                                  <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize">
                                    {skill.skill_name}
                                  </span>
                                </div>
                              </div>

                              <div className='flex flex-col'>
                                <div className='flex items-center gap-2 my-3 flex-wrap'>
                                  {ratingOptions.map((option) => (
                                    <RatingButton
                                      key={option.value}
                                      label={option.label}
                                      value={option.value}
                                      selectedValue={skillsData[skill.id]?.rating}
                                      onClick={(value) => handleRatingSelect(skill.id, value)}
                                    />
                                  ))}
                                </div>
                                {validationErrors[`rating_${skill.id}`] && (
                                  <p className="text-red-500 text-xs mb-2 mt-[-6px]">Please select a rating</p>
                                )}
                              </div>

                              <AutoResizeTextarea
                                value={skillsData[skill.id]?.comment || ""}
                                onChange={(e) => handleCommentChange(skill.id, e.target.value)}
                                placeholder="Write a comment about this skill"
                                isRequired={commentRequiredSkills[skill.id]}
                                hasError={validationErrors[`comment_${skill.id}`]}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-4">No skills found.</div>
                  )}

                  <div className='mt-4 pb-4 px-4 flex justify-end'>
                    <button
                      className="group inline-flex shrink-0 select-none justify-end items-center px-[14px] py-1 text-[13px] font-medium h-[32px] leading-6 transition-colors duration-100 focus:outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none gap-0 rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                      type="button"
                      onClick={handleSaveScorecard}
                    >
                      <div className='flex items-center gap-[6px]'>
                        Save scorecard
                        {savingData && (
                          <div className="simple-spinner">
                            <span></span>
                          </div>
                        )}
                      </div>
                    </button>
                  </div>

                </motion.div>
              )}
            </AnimatePresence>



            {selectedScorecard.skills && selectedScorecard.skills.length > 0 && (
              <>
                <div className='bg-[#e0e0e0] w-full h-[1px]'></div>
                <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize px-4 pt-3 block mb-[-2px]">Skills</span>
              </>
            )}


            {selectedScorecard.skills && selectedScorecard.skills.length > 0 ? (
              <div className="w-full p-4 space-y-2 ">
                {selectedScorecard.skills.map((skill, idx) => {

                  const styles = getSkillScaleBadgeStyles(skill.skill_value);

                  return (
                    <div className="flex flex-col gap-y-2" key={idx}>
                      <div className="p-2 rounded-[0.375rem] bg-white border border-solid border-[#e3e3e3] relative pl-4 feedback-tags-hover 
                                  ">
                        <div className="flex flex-row items-center justify-between flex-grow shrink-0">
                          <div className="flex items-center gap-x-[16px]">
                            <div className=" shrink-0 w-[9px] h-[9px] rounded-full" style={{ background: skill.skill_colour }} />
                            <span className="flex-shrink whitespace-nowrap overflow-hidden text-ellipsis font-normal text-[#1b1b1b] text-sm leading-5 capitalize">
                              {skill.skill_name}
                            </span>

                            <div className=" cursor-default select-none flex flex-row" />
                          </div>
                          {
                            skill.skill_value &&
                            <span
                              className={`inline-flex items-center rounded-md ${styles.bg} px-2 py-1 text-xs font-normal ${styles.text} ring-1 ring-inset ${styles.ring}`}
                            >
                              {getSkillLevelLabel(skill.skill_value)}
                            </span>
                          }

                        </div>
                      </div>
                    </div>
                  )

                })}
              </div>
            ) : <span className="text-[#0000007a] text-[13px] text-center block  py-4">Nothing here</span>}

            {selectedScorecard.appScorecards && selectedScorecard.appScorecards.length > 0 && (
              <>
                {selectedScorecard.appScorecards && selectedScorecard.appScorecards.length > 0 && (
                  <>
                    <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div>

                    <div className="w-full">
                      {selectedScorecard.appScorecards.map((appScorecard, idx) => {
                        const reviewerName = getUserName(appScorecard.assigned_to);
                        const appStageName = appScorecard.stage_id ? getStageName(appScorecard.stage_id) : stageName;
                        const styles1 = getRatingBadgeStyles(appScorecard.rating);
                        const isAccordionOpen = openAccordionIds1[appScorecard.id] || false;

                        return (
                          <div key={idx} className="flex items-center gap-x-2 border-b border-light-gray-200 last-of-type:border-b-0">
                            <div key={appScorecard.id || idx} className="w-full">
                              <ul className="list-none w-full  px-4">
                                <li className="flex flex-row items-start justify-between text-[0.875rem] px-[0rem] py-4 w-full">

                                  <div className="flex-1 px-0 flex flex-col gap-y-[4px]">
                                    <div className="flex items-center gap-x-2 mt-[-2px]">
                                      <p className="text-[0.8125rem] font-medium leading-4 text-[#1b1b1b] capitalize">
                                        {reviewerName}
                                      </p>

                                    </div>
                                    <span className="inline-flex w-fit items-center rounded-md px-1 py-1 text-xs font-medium bg-gray-50 text-gray-600 ring-gray-500/10 capitalize ring-1 ring-inset ">
                                      {appStageName}
                                    </span>
                                    {
                                      appScorecard.comment &&
                                      <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                                        "{appScorecard.comment}"
                                      </p>
                                    }


                                  </div>
                                  <div className="flex gap-1 items-center">

                                    {
                                      appScorecard.rating &&
                                      <span
                                        className={`inline-flex items-center rounded-md ${styles1.bg} px-2 py-1 text-xs font-normal ${styles1.text} ring-1 ring-inset ${styles1.ring}`}
                                      >
                                        {getRatingLabel(appScorecard.rating)}
                                      </span>
                                    }



                                    {
                                      appScorecard.status === 'pending' &&
                                      <span
                                        onClick={() => handleCreateSkillOpen(selectedScorecard.id)}
                                        className="inline-flex mt-[0px] w-fit items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20 capitalize"
                                      >
                                        {appScorecard.status}
                                      </span>

                                    }
                                    <span className="table-tooltip before:!bottom-[28px] before:!left-[8px]"
                                      data-title='skills'>
                                      <button
                                        onClick={() => handleAppSkillRating(appScorecard.id, selectedScorecard)}
                                        className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                                       outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                                       [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 w-6">
                                        <motion.div
                                          animate={{ rotate: isAccordionOpen ? 180 : 0 }}

                                          transition={{ duration: 0.3 }}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width={16}
                                            height={16}
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[0.9rem] h-[0.9rem] text-[#808080]"
                                          >
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M6 9l6 6l6 -6" />
                                          </svg>

                                        </motion.div>
                                      </button>

                                    </span>

                                  </div>

                                </li>

                              </ul>
                              <AnimatePresence>
                                {isAccordionOpen && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    style={{ overflow: 'hidden' }}
                                  >
                                    <div className='bg-[#e0e0e0] w-full h-[1px]'></div>
                                    {loadingAppScorecards[appScorecard?.id] ? (
                                      <div className="flex items-center justify-center py-4">
                                        <div className="simple-spinner-loader">
                                          <span></span>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {appScorecardData && appScorecardData.length > 0 && (
                                          <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize px-4 pt-3 block mb-[-2px]">
                                            Skills
                                          </span>
                                        )}

                                        {appScorecardData && appScorecardData.length > 0 ? (
                                          <div className="w-full p-4 space-y-2">
                                            {appScorecardData.map((skill, idx) => {
                                              const styles1 = getRatingBadgeStyles(skill.skill_scale);

                                              return (
                                                <div className="flex flex-col gap-y-2" key={idx}>
                                                  <div className="p-2 flex items-center rounded-[0.375rem] bg-white border border-solid border-[#e3e3e3] relative pl-4 feedback-tags-hover">
                                                    <div className="flex flex-row items-center justify-between flex-grow shrink-0">
                                                      <div className="flex items-start gap-x-[8px]">
                                                        <div
                                                          className="h-[8px] w-[8px] rounded-[2px] mt-[4px]"
                                                          style={{ background: skill.skill_colour }}
                                                        />
                                                        <div className="flex flex-col gap-y-1">
                                                          <span className="flex-shrink whitespace-nowrap overflow-hidden text-ellipsis font-normal text-[#1b1b1b] text-sm leading-4 capitalize">
                                                            {skill.skill_name}
                                                          </span>
                                                          {
                                                            skill.comment &&
                                                            <span className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                                                              "{skill.comment}"
                                                            </span>
                                                          }


                                                        </div>
                                                        <div className="cursor-default select-none flex flex-row" />

                                                      </div>
                                                    </div>
                                                    {
                                                      skill.skill_scale &&
                                                      <span
                                                        className={`inline-flex items-center rounded-md ${styles1.bg} px-2 py-1 text-xs font-normal ${styles1.text} ring-1 ring-inset ${styles1.ring}`}
                                                      >
                                                        {getRatingLabel(skill.skill_scale)}
                                                      </span>
                                                    }
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <span className="text-[#0000007a] text-[13px] text-center block py-4">
                                            Nothing here
                                          </span>
                                        )}
                                      </>
                                    )}


                                  </motion.div>
                                )}
                              </AnimatePresence>

                            </div>

                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )} */}





      {/* If there's no questions */}
      {(!answerData.questions || answerData.questions.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          No questions found in this questionnaire.
        </div>
      )}
    </div>
  );
}

export default QuestionSetDetails;